import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { Button, Container, Dialog, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { BANK_CASH, GENDER_DROPDOWN, RECEIPT_MODE } from '../../../../../constants';
import { useLocation } from 'react-router-dom';

const BILL_TABLE_HEADER = [
  {
    header: 'Test Name',
    accessor: 'test_name'
  },
  {
    header: 'Specimen',
    accessor: 'specimen'
  },
  {
    header: 'Sample Collected',
    accessor: 'sample_collected'
  },
  {
    header: 'NSample Collected',
    accessor: 'nsample_collected'
  },
  {
    header: 'Sample Received',
    accessor: 'sample_recieved'
  },
  {
    header: 'NSample Received',
    accessor: 'nsample_recieved'
  },
  {
    header: 'Result Entered',
    accessor: 'result_entered'
  },
  {
    header: 'NResult Entered',
    accessor: 'nresult_entered'
  },
  {
    header: 'Validated',
    accessor: 'validated'
  },
  {
    header: 'NValidated',
    accessor: 'n_validated'
  },
  {
    header: 'Signed Off',
    accessor: 'signed_off'
  },
  {
    header: 'NSigned Off',
    accessor: 'n_signed_off'
  }
];

const TABLE_HEADER = [
  {
    header: 'Bill No',
    accessor: 'bill_no'
  },
  {
    header: 'Bill Date',
    accessor: (row) => `${getCurrentDateTime('date', row.createdAt)}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Patient Name',
    accessor: 'patient.name'
  },
  {
    header: 'Sponsor',
    accessor: 'patient.sponsor'
  },
  {
    header: 'Mobile No',
    accessor: 'patient.mobile_no'
  },
  {
    header: 'Net Amount',
    accessor: 'net_amt'
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const initialInvestiagtionBookingDataObj = {
  data: [],
  loading: false
};

export const LabTestDateChange = () => {
  const doctorIDRef = useRef();
  const { pathname } = useLocation();
  const [addDetailsDialogOpen, setAddDetailsDialogOpen] = useState(false);
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [investigationBookingDataObj, setInvestigationBookingDataObj] = useState(initialInvestiagtionBookingDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [bookingDetailList, setBookingDetailList] = useState([]);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        uhid: Yup.string().required(' '),
        patient_name: Yup.string().required(' '),
        opd_type: Yup.string().required(' '),
        ref_type: Yup.string().required(' '),
        doctor: Yup.string().required(' '),
        sponsor: Yup.string().required(' '),
        department: Yup.string().required(' '),
        bill: Yup.object().shape({
          rec_amt_1: Yup.number().min(0, ' '),
          rec_amt_2: Yup.number().min(0, ' '),
          pay_mode: Yup.string().required(' ')
        })
      })
    ),
    defaultValues: {
      date: getCurrentDateTime('date'),
      time: getCurrentDateTime('time'),
      bill: {
        out_sourced: 'No',
        rec_amt_1: 0,
        rec_amt_2: 0,
        tds_amt: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  useEffect(() => {
    let total_qty = 0;
    let total_amt = 0;
    let disc_amt = 0;
    let net_amt = 0;
    let gst_amt = 0;
    detailList.forEach((detail) => {
      total_qty += detail.quantity;
      total_amt += detail.amount;
      disc_amt += detail.disc_amt;
      gst_amt += detail.gst_amt;
      net_amt += detail.net_amt;
    });

    setValue('bill.total_qty', total_qty);
    setValue('bill.total_amt', total_amt);
    setValue('bill.disc_amt', disc_amt);
    setValue('bill.gst_amt', gst_amt);
    setValue('bill.net_amt', net_amt);
  }, [detailList, setValue]);

  const netAmtWatch = watch('bill.net_amt');
  const recAmt1Watch = watch('bill.rec_amt_1');
  const recAmt2Watch = watch('bill.rec_amt_2');
  useEffect(() => {
    const total_recpt = +recAmt1Watch + +recAmt2Watch;
    setValue('bill.total_recpt', total_recpt);
    setValue('bill.balance_amt', netAmtWatch - total_recpt);
  }, [netAmtWatch, recAmt1Watch, recAmt2Watch, setValue]);

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, 'bill', [
        '__v',
        '_id',
        'createdAt',
        'status',
        'service_items',
        'uhid'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('uhid', selectedRecord.uhid);
      setDetailList(selectedRecord.service_items);
      setValue('date', getCurrentDateTime('date', selectedRecord.createdAt));
      setValue('time', getCurrentDateTime('time', selectedRecord.createdAt));
    } else {
      reset();
    }
  }, [selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    const payload = { uhid: formValues.uhid, ...formValues.bill, service_items: detailList };
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/' : '/', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: payload
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'edited' : 'created'} Booking `, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
    } else {
      toast(`Failed to ${editFlag ? 'edit' : 'create'} Booking`, { variant: 'error' });
    }
    setLoading(false);
  };

  const handleRowDelete = (rowId) => {
    setDetailList((prev) => prev.filter((data) => data.id !== rowId));
  };

  const onDetailAddHandler = (data) => {
    if (detailList.some((detail) => detail.service === data.service)) {
      toast('Same service already taken.', { variant: 'error' });
    } else {
      setDetailList((prev) => [...prev, { ...data }]);
    }
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Booking', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Booking', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setInvestigationBookingDataObj({ loading: true });
    const response = await request('/', { method: 'GET' });
    if (response.success) {
      setInvestigationBookingDataObj({ loading: false, data: response.data });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setInvestigationBookingDataObj({ loading: false });
    }
  };

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 2,
            p: 0.2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: "100%",
            boxSizing: 'border-box',
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 3,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3">
                Lab Test Date Change
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button variant="contained">Apply</Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={3} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.32, fontWeight: 700 }}>Search Patient</Typography>
                  <TextField sx={{ flex: 0.7 }} name="search_patient" disabled />
                  <Button sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }} variant="contained">
                    <SearchIcon />
                  </Button>
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Inv Booking No</Typography>
                  <TextField sx={{ flex: 0.8 }} name="uhid" placeholder="Search by Uhid" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Department</Typography>
                  <TextField sx={{ flex: 0.8 }} name="department" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Remarks</Typography>
                  <TextField sx={{ flex: 0.8 }} name="remarks" />
                </Stack>
              </Grid>
              <Grid item xs={3} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700 }}>Adm/Op No</Typography>
                  <TextField disabled={true} sx={{ flex: 0.8 }} name="adm_no" />
                </Stack>

                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700 }}>Barcode No</Typography>
                  <TextField sx={{ flex: 0.8 }} name="barcode_no" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700 }}>Ward No</Typography>
                  <TextField sx={{ flex: 0.8 }} name="ward_no" disabled />
                </Stack>
              </Grid>
              <Grid item xs={3} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Patient Name</Typography>
                  <TextField sx={{ flex: 0.8 }} name="patient_name" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Bill Date</Typography>
                  <TextField sx={{ flex: 0.8 }} name="bill_date" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Sponsor Name</Typography>
                  <TextField sx={{ flex: 0.8 }} name="sponsor_Name" disabled />
                </Stack>
              </Grid>
              <Grid item xs={3} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Age/Mobile No</Typography>
                  <TextField sx={{ flex: 0.4 }} name="age" disabled />
                  <TextField sx={{ flex: 0.4 }} name="mobile_no" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>HMS Bill No</Typography>
                  <TextField sx={{ flex: 0.8 }} name="hms_bill_no" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700 }}>Paymode</Typography>
                  <TextField sx={{ flex: 0.8 }} name="paymode" disabled />
                </Stack>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: '30rem' }}>
                <Table
                  containerSX={{ flex: 1, border: '1px solid black' }}
                  data={detailList}
                  columns={BILL_TABLE_HEADER}
                  size="small"
                  showDeleteAction={true}
                  onDeleteHandler={handleRowDelete}
                />
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={1.5}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            OPD Billing Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 3 }}
          columns={TABLE_HEADER}
          data={investigationBookingDataObj.data || []}
          loading={investigationBookingDataObj.loading}
          onRowClick={(row) => {
            delete row.patient;
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
      <Dialog
        open={addDetailsDialogOpen}
        onClose={() => setAddDetailsDialogOpen(false)}
        PaperProps={{ sx: { width: '80%', height: '90vh', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      ></Dialog>
      <Dialog PaperProps={{ sx: { width: '80%', p: 2 } }} maxWidth="xl" scroll="body">
        <Stack
          padding={1.5}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Patient Query Window
          </Typography>
        </Stack>
      </Dialog>
    </>
  );
};
